import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface ScenarioSummary {
  tenYearNetBenefits: number;
  tenYearPresentValue: number;
  totalIncentive: number;
  tenYearGrossNetBenefits: number;
  perJobIncentiveCost: number;
  rateOfReturn: number | null;
  paybackPeriodYears: number | null;
  grandTotalCityBenefit: number;
}

interface ScenarioResult {
  summary: ScenarioSummary;
}

interface Scenario {
  id: string;
  name: string;
  description?: string | null;
  status: 'draft' | 'ready';
  results?: ScenarioResult | null;
  projectType?: 'new_startup' | 'expansion' | 'retention';
}

interface BaseAnalysis {
  projectName: string;
  projectType?: 'new_startup' | 'expansion' | 'retention';
  results?: { summary: ScenarioSummary } | null;
}

interface CompareResponse {
  base: BaseAnalysis;
  scenarios: Scenario[];
}

function fmtCurrency(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function fmtNum(n: number, dec = 1) {
  return n.toLocaleString('en-US', { maximumFractionDigits: dec });
}

function delta(scenario: number, base: number): string {
  const d = scenario - base;
  const pct = base !== 0 ? ((d / Math.abs(base)) * 100).toFixed(1) : '—';
  const prefix = d >= 0 ? '+' : '';
  return `${prefix}${fmtCurrency(d)} (${prefix}${pct}%)`;
}

function deltaClass(scenario: number, base: number, higherIsBetter = true): string {
  const better = higherIsBetter ? scenario > base : scenario < base;
  if (scenario === base) return 'text-slate-400';
  return better ? 'text-green-600' : 'text-red-600';
}

const SCENARIO_COLORS = ['#6366f1', '#10b981', '#f59e0b'];

const KPI_ROWS: {
  key: keyof ScenarioSummary;
  label: string;
  fmt: (v: number | null) => string;
  higherIsBetter: boolean;
}[] = [
  {
    key: 'tenYearNetBenefits',
    label: '10-yr net benefit',
    fmt: (v) => (v != null ? fmtCurrency(v) : '—'),
    higherIsBetter: true,
  },
  {
    key: 'tenYearPresentValue',
    label: '10-yr present value',
    fmt: (v) => (v != null ? fmtCurrency(v) : '—'),
    higherIsBetter: true,
  },
  {
    key: 'grandTotalCityBenefit',
    label: 'Grand total city benefit',
    fmt: (v) => (v != null ? fmtCurrency(v) : '—'),
    higherIsBetter: true,
  },
  {
    key: 'totalIncentive',
    label: 'Total incentive cost',
    fmt: (v) => (v != null ? fmtCurrency(v) : '—'),
    higherIsBetter: false,
  },
  {
    key: 'perJobIncentiveCost',
    label: 'Cost per job',
    fmt: (v) => (v != null && v > 0 ? fmtCurrency(v) : '—'),
    higherIsBetter: false,
  },
  {
    key: 'rateOfReturn',
    label: 'Rate of return',
    fmt: (v) => (v != null ? `${(v * 100).toFixed(1)}%` : 'N/A'),
    higherIsBetter: true,
  },
  {
    key: 'paybackPeriodYears',
    label: 'Payback period',
    fmt: (v) => (v != null ? `${fmtNum(v)} yrs` : 'N/A'),
    higherIsBetter: false,
  },
];

export function ScenarioCompareRoute() {
  const { id: analysisId } = useParams<{ id: string }>();
  const getToken = useSessionTokenGetter();

  const [allScenarios, setAllScenarios] = useState<Scenario[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [compareData, setCompareData] = useState<CompareResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all ready scenarios
  useEffect(() => {
    if (!analysisId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${analysisId}/scenarios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const json = await res.json();
        const ready = (json.scenarios as Scenario[]).filter((s) => s.status === 'ready');
        if (!cancelled) {
          setAllScenarios(ready);
          // Auto-select up to 3
          setSelected(ready.slice(0, 3).map((s) => s.id));
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [analysisId, getToken]);

  // Run comparison whenever selected changes (and has ≥1 scenario)
  useEffect(() => {
    if (selected.length === 0 || !analysisId) return;
    let cancelled = false;
    setComparing(true);
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${analysisId}/scenarios/compare`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ scenarioIds: selected }),
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const json = await res.json();
        if (!cancelled) setCompareData(json as CompareResponse);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setComparing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, analysisId, getToken]);

  function toggleScenario(id: string) {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev; // max 3
      return [...prev, id];
    });
  }

  const baseSummary = compareData?.base.results?.summary ?? null;

  // Build chart data — Net Benefit + Present Value comparison
  const chartData = useMemo(() => {
    if (!compareData) return [];
    const rows: { name: string; 'Net Benefit': number; 'Present Value': number }[] = [];
    if (baseSummary) {
      rows.push({
        name: 'Base',
        'Net Benefit': baseSummary.tenYearNetBenefits,
        'Present Value': baseSummary.tenYearPresentValue,
      });
    }
    for (const s of compareData.scenarios) {
      if (s.results?.summary) {
        rows.push({
          name: s.name.length > 18 ? `${s.name.slice(0, 16)}…` : s.name,
          'Net Benefit': s.results.summary.tenYearNetBenefits,
          'Present Value': s.results.summary.tenYearPresentValue,
        });
      }
    }
    return rows;
  }, [compareData, baseSummary]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={`/analyses/${analysisId}/scenarios`}
            className="text-xs text-slate-400 hover:underline"
          >
            ← Scenarios
          </Link>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight">Scenario Comparison</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Select up to 3 ready scenarios to compare against the base analysis.
          </p>
        </div>
      </div>

      {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {/* ADR-031 Phase 5 — cross-type guard. Retention uses avoided-loss
          framing; greenfield/expansion use net-benefit framing. Displaying
          their "10-yr net benefit" side by side is misleading without a
          clear warning. */}
      {compareData &&
        (() => {
          const types = new Set<string>();
          if (compareData.base.projectType) types.add(compareData.base.projectType);
          for (const s of compareData.scenarios) if (s.projectType) types.add(s.projectType);
          const mixesRetention = types.has('retention') && types.size > 1;
          if (!mixesRetention) return null;
          return (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-white">
                  !
                </div>
                <div className="text-xs text-amber-900">
                  <strong>Cross-type comparison.</strong> This view mixes retention (avoided-loss
                  framing) with new-startup or expansion (net-benefit framing). The "10-yr net
                  benefit" column represents fundamentally different counterfactuals — retention
                  numbers are risk-weighted avoided losses, not new activity. Interpret deltas with
                  care and do not sum across rows.
                </div>
              </div>
            </div>
          );
        })()}

      {loading ? (
        <p className="text-sm text-slate-500">Loading scenarios…</p>
      ) : allScenarios.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
          <p className="text-sm text-slate-500">
            No ready scenarios yet. Save & Run some scenarios first.
          </p>
          <Link
            to={`/analyses/${analysisId}/scenarios`}
            className="mt-3 inline-block text-sm text-indigo-600 hover:underline"
          >
            Back to scenarios →
          </Link>
        </div>
      ) : (
        <>
          {/* Scenario picker */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Select scenarios ({selected.length}/3)
            </p>
            <div className="flex flex-wrap gap-2">
              {allScenarios.map((s) => {
                const isSelected = selected.includes(s.id);
                const color = SCENARIO_COLORS[selected.indexOf(s.id)] ?? '#cbd5e1';
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleScenario(s.id)}
                    disabled={!isSelected && selected.length >= 3}
                    style={isSelected ? { borderColor: color, backgroundColor: `${color}15` } : {}}
                    className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors disabled:opacity-40 ${
                      isSelected
                        ? 'text-slate-900'
                        : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Comparison table */}
          {compareData && selected.length > 0 && (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-500">Metric</th>
                      <th className="px-4 py-3 text-right font-medium text-slate-500">Base</th>
                      {compareData.scenarios.map((s, i) => (
                        <th
                          key={s.id}
                          className="px-4 py-3 text-right font-medium"
                          style={{ color: SCENARIO_COLORS[i] }}
                        >
                          {s.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {KPI_ROWS.map((row) => {
                      const baseVal = baseSummary ? (baseSummary[row.key] as number | null) : null;
                      return (
                        <tr key={row.key} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-700">{row.label}</td>
                          <td className="px-4 py-3 text-right text-slate-600">
                            {row.fmt(baseVal)}
                          </td>
                          {compareData.scenarios.map((s) => {
                            const val = s.results?.summary
                              ? (s.results.summary[row.key] as number | null)
                              : null;
                            const showDelta = baseVal != null && val != null;
                            return (
                              <td key={s.id} className="px-4 py-3 text-right">
                                <div className="font-medium text-slate-900">{row.fmt(val)}</div>
                                {showDelta && val != null && baseVal != null && (
                                  <div
                                    className={`text-xs ${deltaClass(val, baseVal, row.higherIsBetter)}`}
                                  >
                                    {delta(val, baseVal)}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Chart */}
              {chartData.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                    10-Year Benefit Comparison
                  </h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData} margin={{ top: 4, right: 24, bottom: 4, left: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v: number) =>
                          Math.abs(v) >= 1_000_000
                            ? `$${(v / 1_000_000).toFixed(1)}M`
                            : `$${(v / 1_000).toFixed(0)}K`
                        }
                      />
                      <Tooltip formatter={(v) => [fmtCurrency(Number(v)), '']} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Net Benefit" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Present Value" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="mt-2 text-xs text-slate-400">
                    Delta rows show scenario − base (green = better, red = worse for each metric).
                  </p>
                </div>
              )}
            </>
          )}

          {comparing && <p className="text-sm text-slate-400">Refreshing comparison…</p>}
        </>
      )}
    </div>
  );
}
